import React from 'react';
import {modalConfirm} from 'app/utils/modals/popups';
import I18n from 'app/i18n';
import csrfToken from 'app/helpers/csrf-token';
import {locationQuery} from 'app/utils/navigate/location';
import {toastSuccess, toastError} from 'app/components/toasts/liteToast';
import Post from 'app/utils/Post';
import Delete from 'app/utils/Delete';
import Get from 'app/utils/Get';
import {userStore} from 'app/store/global';

const STEAM_AUTH_FORM_TARGET_TOP = '_top';

export const startSteamAuthFlow = (target: string = STEAM_AUTH_FORM_TARGET_TOP): void => {
    const form = document.createElement('form');

    form.method = 'post';
    form.action = '/auth/steam';
    form.target = target;

    const tokenInput = document.createElement('input');

    tokenInput.type = 'hidden';
    tokenInput.name = 'authenticity_token';
    tokenInput.value = csrfToken() || '';
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
    form.remove();
};

interface PendingLinkData {
    success?: boolean
    steam_id?: string
    nickname?: string | null
    avatar?: string | null
    error?: string
}

const ERROR_KEYS = [
    'failed_to_link_account',
    'steam_already_linked',
    'user_already_linked',
    'steam_unavailable',
    'invalid_or_expired_link'
];

const steamErrorMessage = (key: string): string => {
    if (ERROR_KEYS.includes(key)) {
        return I18n.t(`steam.errors.${key}`);
    }

    return I18n.t('steam.errors.failed_to_link_account');
};

const fetchPendingLinkData = (): Promise<PendingLinkData> => new Get({url: '/steam/pending_link'})
    .execute()
    .then(response => response.json());

const clearSteamLinkQueryParams = (): void => {
    const url = new URL(window.location.href);

    url.searchParams.delete('steam_link_pending');
    url.searchParams.delete('steam_link_error');
    window.history.replaceState({}, '', url.toString());
};

export const showSteamLinkConfirmation = (steamLinked: boolean): void => {
    const params = locationQuery();
    const pending = params.steam_link_pending;
    const errorKey = params.steam_link_error;

    if (!pending && !errorKey) {
        return;
    }

    clearSteamLinkQueryParams();

    if (errorKey) {
        toastError(steamErrorMessage(errorKey));
        return;
    }

    if (steamLinked) {
        toastError(I18n.t('steam.errors.user_already_linked'));
        return;
    }

    fetchPendingLinkData()
        .then((data: PendingLinkData) => {
            if (!data.success) {
                toastError(data.error || I18n.t('steam.errors.invalid_or_expired_link'));
                return;
            }

            showPendingConfirmPopup(data);
        })
        .catch(() => {
            toastError(I18n.t('steam.errors.steam_unavailable'));
        });
};

const showPendingConfirmPopup = (data: PendingLinkData): void => {
    const content = {
        element: <div className="steam-link-confirm">
            {data.avatar && <img
                src={data.avatar}
                alt={data.nickname || 'Steam'}
                width={64}
                height={64}
            />}
            <p>{I18n.t('steam.labels.confirm_link_account')}</p>
            {data.nickname && <p><strong>{data.nickname}</strong></p>}
        </div>,
        react: true
    };

    modalConfirm(
        content,
        () => confirmSteamLink(),
        () => cancelSteamLink()
    );
};

const confirmSteamLink = (): void => {
    new Post({url: '/steam/link_account'})
        .execute()
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                toastSuccess(data.message || I18n.t('steam.success.account_linked'));
                userStore.updateProfile();
            } else {
                toastError(data.error || I18n.t('steam.errors.failed_to_link_account'));
            }
        })
        .catch(() => {
            toastError(I18n.t('steam.errors.failed_to_link_account'));
        });
};

const cancelSteamLink = (): void => {
    new Delete({url: '/steam/cancel_link'})
        .execute()
        .catch(() => null);
};
